/**
 * A contact's flag as one venue sees it — the twin of `effective_contact_flag` in
 * `bookmyband-api/app/utils/contact_identity.py`; change one, change the other.
 *
 * Expired and do-not-contact describe the address and win everywhere. Otherwise a contact listed
 * in the venue's `gatekeeper_contact_ids` is that venue's gatekeeper — a role held per venue, so
 * the same town hall can be the city's gatekeeper and its Comité des Fêtes' active contact.
 * Otherwise the contact's own status applies (a not-yet-migrated contact may still store
 * `gatekeeper`, meaning gatekeeper everywhere). See docs/CONTACT_IDENTITY.md.
 */
import type { Contact, ContactFlag, Venue } from '../types';

const ADDRESS_BLOCKING_FLAGS: ContactFlag[] = ['expired', 'do_not_contact'];

type RoleSource = Pick<Venue, 'gatekeeper_contact_ids'> | null | undefined;

export function effectiveFlag(contact: Pick<Contact, '_id' | 'flags'>, venue: RoleSource): ContactFlag {
  const stored = contact.flags ?? 'none';
  if (ADDRESS_BLOCKING_FLAGS.includes(stored)) return stored;
  if (venue?.gatekeeper_contact_ids?.includes(contact._id)) return 'gatekeeper';
  return stored;
}

/** The contacts with `flags` read at *venue*, for the places reasoning about a venue's contacts. */
export function withVenueFlags<T extends Pick<Contact, '_id' | 'flags'>>(contacts: T[], venue: RoleSource): T[] {
  return contacts.map(contact => ({ ...contact, flags: effectiveFlag(contact, venue) }));
}
